import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import moment from 'moment';
import _ from 'lodash';
import { Tooltip, OverlayTrigger } from 'react-bootstrap';

import CountDownTimer from '../util/timer';
import * as BECaller from '../util/apiCaller';
import { getJiraCloudSource } from '../util/dashboardUtils';
import { SourceDropdown } from './SourceDropdown';

import syncIcon from '../assets/images/sync-info.svg';

const MAX_RETRIES = 10;

export const SOURCE_HINT = 'Click to see where the data comes from.';

export class NextSync extends Component {
  constructor(props) {
    super(props);

    this.state = {
      loading: true,
      noJob: true,
      inProgress: false
    };
  }

  retries = 0;
  timer = { destroy: () => {} };
  timeout;

  componentDidMount() {
    if ((this.props.app === 'jira' || this.props.app === 'azure') && this.props.dashboard && this.props.dashboard._id) {
      this.init();
    }
  }

  componentDidUpdate(prevProps) {
    let prevDashboard = prevProps.dashboard || {};
    if (
      (this.props.app === 'jira' || this.props.app === 'azure') &&
      this.props.dashboard &&
      this.props.dashboard._id &&
      (prevDashboard._id != this.props.dashboard._id || prevProps.lastUpdated != this.props.lastUpdated)
    ) {
      this.init();
    }
  }

  componentWillUnmount() {
    if (this.timer) {
      this.timer.destroy();
      !!this.timeout && clearTimeout(this.timeout);
    }
  }

  init() {
    if (this.timer) {
      this.timer.destroy();
      !!this.timeout && clearTimeout(this.timeout);
    }
    this.retries = 0;
    this.setState({ noJob: false, loading: true });
    this.getNextSyncTime();
  }

  getNextSyncTime() {
    BECaller.api(`dashboards/${this.props.dashboard._id}/nextsync`).then(data => {
      if (data.noJob || data.noMeta) {
        this.setState({ noJob: true });
        return false;
      }

      if (!data.running && !data.nextRunAt && this.retries < MAX_RETRIES) {
        this.retries++;
        this.timeout = setTimeout(() => this.getNextSyncTime(), 10000);
        return false;
      }

      if (data.running) {
        this.setState({ loading: false, inProgress: true });
        this.timeout = setTimeout(() => this.getNextSyncTime(), 5000);
      }

      if (!data.running && data.nextRunAt) {
        this.startTimer(moment(data.nextRunAt).diff(moment(), 'seconds'));
        this.setState({ loading: false, inProgress: false });
      }
    });
  }

  startTimer(duration) {
    this.timer = new CountDownTimer(duration);
    this.timer.onTick((min, sec) => {
      this.setState({
        time: `${min < 10 ? `0${min}` : min}:${sec < 10 ? `0${sec}` : sec}`
      });
    });
    this.timer.onExpire(() => {
      this.setState({ inProgress: true });
      this.timeout = setTimeout(() => this.getNextSyncTime(), 1000);
    });
    this.timer.start();
  }

  getDashboardApp() {
    const { app, dashboard } = this.props;
    return (dashboard && dashboard.application) || app;
  }

  renderSyncLabel(title, value) {
    if (value) {
      return <span>{title}<br />{value}</span>
    } else {
      return <span>{title}</span>;
    }
  }

  renderSyncHint(text) {
    return <Tooltip id="tooltip-settings" className="tooltip_dark max-width-200 text-align-left">{text}</Tooltip>;
  }

  renderSync(title, value, tooltip) {
    const { dashboard, user } = this.props;
    const label = this.renderSyncLabel(title, value);

    const jiraSource = this.getDashboardApp() === 'jira' && getJiraCloudSource(dashboard, user);
    if (jiraSource) {
      const hint = tooltip ? `${tooltip} ${SOURCE_HINT}` : SOURCE_HINT;
      return (
        <SourceDropdown key={dashboard._id} source={jiraSource} hint={this.renderSyncHint(hint)}>
          <img src={syncIcon} alt="#" />
          {label}
        </SourceDropdown>
      );
    }

    const sync = (
      <span className="next_sync">
        <img src={syncIcon} alt="#" />
        {label}
      </span>
    );
    if (!tooltip) {
      return sync;
    }
    return <OverlayTrigger placement="bottom" overlay={this.renderSyncHint(tooltip)}>{sync}</OverlayTrigger>;
  }

  render() {
    const { app, dashboard, lastUpdated } = this.props;
    const { noJob, loading, inProgress, time } = this.state;
    const appName = _.upperFirst(this.getDashboardApp());

    if (app === 'file' && lastUpdated) {
      return this.renderSync(
        'Last updated',
        moment(lastUpdated).fromNow(),
        'The dashboard reflects data from the last file upload.'
      );
    }

    if (noJob && dashboard && (dashboard.lastWebhookReceived || dashboard.lastSynced || lastUpdated)) {
      const detected = Math.max(+new Date(dashboard.lastWebhookReceived || 0), +new Date(dashboard.lastSynced || 0), +new Date(lastUpdated || 0));
      return this.renderSync(
        'Last update detected',
        moment(detected).fromNow(),
        `The dashboard syncs in real-time via webhooks and updates only when changes occur on your ${appName} board.`
      );
    }

    if (loading || (app !== 'jira' && app !== 'azure')) {
      return <span className='next_sync none'></span>;
    }

    if (inProgress) {
      return this.renderSync('Syncing in progress...');
    }

    return this.renderSync(
      'Next update in',
      `${time} min`,
      `The dashboard pulls changes from your ${appName} board every 30 minutes.`,
    );
  }
}

export function mapStateToProps(state) {
  return {
    app: state.app.app,
    lastUpdated: state.app.lastUpdated,
    dashboard: state.dashboards.selected,
    user: state.auth.user
  };
}

export default connect(mapStateToProps)(NextSync);
